import React from "react"
import {
  Stack, Flex, Text, Box, Input, useNumberInput,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Button from "components/Buttons/Button"
import Link from "components/elements/Link"
import { asUsd } from "utilities/strings"
import { purchaseLeadRetrievalLicenses } from "api/events"
import { PAYMENT_DETAILS_LINK } from "utilities/externalLinks"

// Must match API::V3::LeadRetrievalLicensesController::MAX_PURCHASE_QUANTITY
const MAX_QUANTITY = 1000

type Props = {
  eventId: string
  licenseFee: number
  onHide: () => void
}

const PurchaseLeadRetrievalLicenses = ({
  eventId, licenseFee, onHide,
}: Props) => {
  const {
    valueAsNumber, getInputProps, getIncrementButtonProps, getDecrementButtonProps,
  } = useNumberInput({
    step: 1, defaultValue: 1, min: 1, max: MAX_QUANTITY,
  })
  const quantity = Number.isNaN(valueAsNumber) ? 1 : valueAsNumber
  const total = quantity * licenseFee

  const handlePay = async () => {
    const { data } = await purchaseLeadRetrievalLicenses(eventId, quantity)
    window.location.href = data.url
  }

  return (
    <ModalForm
      title="Purchase Licenses"
      onClose={onHide}
      onSubmit={handlePay}
      submitText="Continue to Payment"
      initialValues={{}}
      size="lg"
    >
      {() => (
        <Stack spacing={0}>
          <Text mb={6}>
            Buy additional Lead Retrieval licenses and assign them to exhibitors at this event.
          </Text>
          <Box borderWidth={1} borderRadius="md" overflow="hidden">
            <Flex justifyContent="space-between" alignItems="center" px={6} py={5}>
              <Box>
                <Text fontWeight="bold">Lead Retrieval licenses</Text>
                <Text color="gray.500">{asUsd(licenseFee)} per license</Text>
              </Box>
              <Flex borderWidth={1} borderRadius="md" overflow="hidden">
                <Button
                  {...getDecrementButtonProps()}
                  variant="ghost"
                  colorScheme="button.light"
                  borderRadius={0}
                  borderRightWidth={1}
                  bg="gray.50"
                  fontSize="lg"
                  fontWeight="bold"
                >
                  −
                </Button>
                <Input
                  {...getInputProps()}
                  textAlign="center"
                  border="none"
                  borderRadius={0}
                  w={16}
                  fontWeight="bold"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                  _focus={{ boxShadow: "none" }}
                  _focusVisible={{ boxShadow: "none" }}
                />
                <Button
                  {...getIncrementButtonProps()}
                  variant="ghost"
                  colorScheme="button.light"
                  borderRadius={0}
                  borderLeftWidth={1}
                  bg="gray.50"
                  fontSize="lg"
                  fontWeight="bold"
                >
                  +
                </Button>
              </Flex>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" px={6} py={4} bg="blue.50" borderTopWidth={1}>
              <Text>Total due</Text>
              <Text fontWeight="bold" fontSize="lg" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {asUsd(total)}
              </Text>
            </Flex>
          </Box>
          <Stack spacing={2} mt={6}>
            <Text>Payment is completed on a secure Stripe checkout page.</Text>
            <Text>
              By purchasing, you agree to the {" "}
              <Link href={PAYMENT_DETAILS_LINK} isExternal>Diobox Terms of Service</Link>.
            </Text>
          </Stack>
        </Stack>
      )}
    </ModalForm>
  )
}

export default PurchaseLeadRetrievalLicenses
